import { CARD_H, CARD_W, ends } from "@/lib/family";

export const LINE_STYLES = {
  descent: { label: "Ancestor → descendant", style: { stroke: "#94a3b8", strokeWidth: 1.75 } },
  subspecies: { label: "Parent → subspecies", style: { stroke: "#38bdf8", strokeWidth: 2.25 } },
  hybrid: { label: "Parents → hybrid", style: { stroke: "#d946ef", strokeWidth: 1.75, strokeDasharray: "6 5" } },
  cousin: { label: "Cousin species", style: { stroke: "#34d399", strokeWidth: 1.5, strokeDasharray: "2 5" } },
  other: { label: "Related", style: { stroke: "#64748b", strokeWidth: 1.25, strokeDasharray: "2 5" } },
};

export const SpeciesLines = ({ edges, pos, hi }) => {
  const P = (id) => pos.get(id);
  const cx = (id) => P(id).x + CARD_W / 2, top = (id) => P(id).y, bot = (id) => top(id) + CARD_H, mid = (id) => top(id) + CARD_H / 2;
  const path = (e) => {
    const [a, b] = ends(e);
    if (e.kind === "pc" && top(e.down) > top(e.up)) {
      const ym = top(e.down) - (e.type === "hybrid" ? 14 : 30);
      return { d: `M${cx(e.up)} ${bot(e.up)} L${cx(e.up)} ${ym} L${cx(e.down)} ${ym} L${cx(e.down)} ${top(e.down)}` };
    }
    if (top(a) === top(b)) {
      const [l, r] = P(a).x < P(b).x ? [a, b] : [b, a];
      // Cards in between would hide a straight line, so bracket over them instead
      if (P(r).x - P(l).x > CARD_W * 1.5) {
        const y = top(l) - 12;
        return { d: `M${cx(l)} ${top(l)} L${cx(l)} ${y} L${cx(r)} ${y} L${cx(r)} ${top(r)}`, lx: cx(l) + 36, ly: y - 4 };
      }
      return { d: `M${P(l).x + CARD_W} ${mid(l)} L${P(r).x} ${mid(r)}`, lx: (P(l).x + CARD_W + P(r).x) / 2, ly: mid(l) - 6 };
    }
    const [u, w] = top(a) < top(b) ? [a, b] : [b, a];
    return { d: `M${cx(u)} ${bot(u)} L${cx(w)} ${top(w)}`, lx: (cx(u) + cx(w)) / 2 + 8, ly: (bot(u) + top(w)) / 2 };
  };
  // Hybrids with two parents on one row: bar between the parents, then one drop to the child
  const shown = edges.filter((e) => ends(e).every((id) => pos.has(id)));
  const joined = new Set(), junctions = [];
  const byChild = {};
  shown.filter((e) => e.type === "hybrid" && top(e.down) > top(e.up)).forEach((e) => (byChild[e.down] ||= []).push(e));
  Object.entries(byChild).forEach(([child, es]) => {
    const [e1, e2] = es;
    if (!e2 || top(e1.up) !== top(e2.up)) return;
    const [l, r] = P(e1.up).x < P(e2.up).x ? [e1.up, e2.up] : [e2.up, e1.up];
    const mx = (P(l).x + CARD_W + P(r).x) / 2, ym = top(child) - 14;
    junctions.push({ ids: [l, r, child], d: `M${P(l).x + CARD_W} ${mid(l)} L${P(r).x} ${mid(r)} M${mx} ${mid(l)} L${mx} ${ym} L${cx(child)} ${ym} L${cx(child)} ${top(child)}` });
    joined.add(e1).add(e2);
  });
  return (
    <g data-testid="tol-lines">
      {junctions.map((j, i) => (
        <g key={`j${i}`} opacity={!hi || j.ids.includes(hi) ? 1 : 0.12} className="transition-opacity duration-300" data-testid="tol-line-hybrid">
          <path d={j.d} fill="none" {...LINE_STYLES.hybrid.style} />
        </g>
      ))}
      {shown.filter((e) => !joined.has(e)).map((e, i) => {
        const l = path(e);
        const on = !hi || ends(e).includes(hi);
        return (
          <g key={i} opacity={on ? 1 : 0.12} className="transition-opacity duration-300" data-testid={`tol-line-${e.type}`}>
            <path d={l.d} fill="none" {...LINE_STYLES[e.type].style} />
            {l.lx !== undefined && e.kind !== "pc" && (
              <text x={l.lx} y={l.ly} textAnchor="middle" fontSize="10" style={{ fill: "rgb(var(--slate-400))" }} fontFamily="JetBrains Mono">{e.type === "cousin" ? "cousin" : "related"}</text>
            )}
          </g>
        );
      })}
    </g>
  );
};
